'use client';
import { useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useRouter } from 'next/navigation';
import CenteredBox from '@/app/base-ui/CenterBox';
import { useHomeData } from '@/app/contexts/home-data-context';
import { useGlobalPostState, PostUpdateType } from '@/app/contexts/global-post-state-context';
import { actionFeedServices } from '@/app/service/actionFeedService';
import { useTranslation } from '@/app/hooks/useTranslation';
import PostGridCard from './PostGridCard';
import { myChannelService } from '@/app/service/myChannelService';
import BaseModal from '@/app/base-ui/BaseModalUI';
import LevelUpModal from '@/app/components/Common/LevelUpModal';
import { useState } from 'react';

const FeaturedGridPosts = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const router = useRouter();
    const { homeData, isInitialLoading, removeFeaturedPost, refreshFeaturedPosts } = useHomeData();
    const globalPostState = useGlobalPostState();

    // Reward states
    const [bonusPoints, setBonusPoints] = useState<number>(0);
    const [showBonusModal, setShowBonusModal] = useState<boolean>(false);
    const [levelUpData, setLevelUpData] = useState<any>(null);
    const [showLevelUpModal, setShowLevelUpModal] = useState<boolean>(false);

    const handleViewAll = () => {
        router.push('/community/posts', { scroll: false });
    };

    // Subscribe to global post state updates
    useEffect(() => {
        const unsubscribe = globalPostState.subscribe((update) => {
            // Refresh featured posts when post is hidden, deleted, or user is blocked
            if (update.type === PostUpdateType.POST_HIDDEN ||
                update.type === PostUpdateType.POST_DELETED ||
                update.type === PostUpdateType.USER_BLOCKED) {
                refreshFeaturedPosts();
            }
        });
        return unsubscribe;
    }, [globalPostState, refreshFeaturedPosts]);

    if (isInitialLoading || !homeData) {
        return (
            <Box sx={{ backgroundColor: 'white', pt: 10, px: { mobile: '20px', tablet: '50px' } }}>
                <CenteredBox sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h2_18Bold" lineHeight="100%">
                        {t('featured_posts', '인기 게시물')}
                    </Typography>
                </CenteredBox>
                <Box sx={{ 
                    display: 'flex',
                    flexWrap: 'wrap',
                    columnGap: '10px',
                    rowGap: 3,
                    alignItems: 'flex-start'
                }}>
                    {[...Array(4)].map((_, index) => (
                        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 'calc(50% - 5px)' }}>
                            <Box sx={{ position: 'relative', width: '100%', height: 0, paddingBottom: '115.15%', backgroundColor: theme.palette.grayscale.gray02, borderRadius: '24px' }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
                                <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: theme.palette.grayscale.gray02 }} />
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box sx={{ width: '60%', height: 14, backgroundColor: theme.palette.grayscale.gray02, borderRadius: '4px' }} />
                                    <Box sx={{ width: '30%', height: 10, backgroundColor: theme.palette.grayscale.gray02, borderRadius: '4px' }} />
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    const posts = homeData.featuredPosts;

    if (posts.length === 0) {
        return null;
    }

    const handleDeletePost = async (postId: string) => {
        try {
            await actionFeedServices.deleteFeedForOnlyPosts({ type: 'posts', id: postId });
            removeFeaturedPost(postId);
            globalPostState.deletePost(postId);
        } catch (error) {
            console.error('Delete post error:', error);
        }
    };

    const handleHidePost = async (postId: string) => {
        try {
            await actionFeedServices.hidePost({ type: 'posts', id: postId });
            removeFeaturedPost(postId);
        } catch (error) {
            console.error('Hide post error:', error);
        }
    };

    const handleLikePost = async (post: any) => {
        try {
            const isLiked = !post.isLike;
            const newLikeCount = isLiked ? (post.likeCount || 0) + 1 : Math.max(0, (post.likeCount || 0) - 1);
            
            // Optimistic update
            globalPostState.updatePostField(post.id, 'isLike', isLiked);
            globalPostState.updatePostField(post.id, 'likeCount', newLikeCount);

            const response = await actionFeedServices.likeFeeds({ 
                id: post.id, 
                type: 'posts' // All items in this grid are treated as posts for liking
            });

            // Handle rewards
            if (isLiked) {
                const responseData = response?.data?.data || response?.data;
                if (responseData?.bonus_point && responseData.bonus_point > 0) {
                    setBonusPoints(responseData.bonus_point);
                    setShowBonusModal(true);
                    if (responseData?.level_up?.level_new) {
                        setLevelUpData(responseData.level_up);
                    }
                } else if (responseData?.level_up?.level_new) {
                    setLevelUpData(responseData.level_up);
                    setShowLevelUpModal(true);
                }
            }
        } catch (error) {
            console.error('Like post error:', error);
            // Revert on error
            globalPostState.updatePostField(post.id, 'isLike', post.isLike);
            globalPostState.updatePostField(post.id, 'likeCount', post.likeCount);
        }
    };

    const handleReportPost = async (postId: string) => {
        try {
            await actionFeedServices.reportPost({ type: 'posts', id: postId });
        } catch (error) {
            console.error('Report post error:', error);
        }
    };

    const handleBlockUser = async () => {
        // Refresh handled by globalPostState subscription
    };

    return (
        <Box sx={{ backgroundColor: 'white', pt: 10, pb: 5 }}>
            {/* Header Section */}
            <Box sx={{ paddingX: { mobile: '20px', tablet: '50px' }, mb: 3 }}>
                <CenteredBox sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h2_18Bold" lineHeight="100%">
                        {t('featured_posts', '인기 게시물')}
                    </Typography>
                    <Box
                        onClick={handleViewAll}
                        sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            '&:hover': {
                                opacity: 0.7,
                            }
                        }}
                    >
                        <Typography variant="h2_12Regular" sx={{ color: theme.palette.grayscale.gray06 }}>
                            {t('view_more', '더보기')}
                        </Typography>
                        <Typography variant="h2_12Regular" sx={{ color: theme.palette.grayscale.gray05 }}>
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.5 7L5.5 4L2.5 1" stroke="#666666" strokeLinejoin="round"/>
                            </svg>
                        </Typography>
                    </Box>
                </CenteredBox>
            </Box>

            {/* Grid Content */}
            <Box sx={{ 
                paddingX: { mobile: '20px', tablet: '50px' },
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: '10px',
                rowGap: 3,
                alignItems: 'flex-start'
            }}>
                {posts.map((item, index) => (
                    <Box key={`${index}${item.id}`} sx={{ width: 'calc(50% - 5px)' }}>
                        <PostGridCard
                            data={item}
                            onDelete={() => handleDeletePost(item.id)}
                            onHiddenPost={handleHidePost}
                            onReport={() => handleReportPost(item.id)}
                            onBlockUser={handleBlockUser}
                            isFollowing={item.owner?.isFollowing}
                            onLike={() => handleLikePost(item)}
                        />
                    </Box>
                ))}
            </Box>

            {/* Like Bonus Points Modal */}
            {showBonusModal && (
                <BaseModal
                    open={showBonusModal}
                    onClose={() => {
                        setShowBonusModal(false);
                        if (levelUpData) {
                            setTimeout(() => {
                                setShowLevelUpModal(true);
                            }, 500);
                        }
                    }}
                    onCancel={() => {
                        setShowBonusModal(false);
                        if (levelUpData) {
                            setTimeout(() => {
                                setShowLevelUpModal(true);
                            }, 500);
                        }
                    }}
                    title={`${bonusPoints.toLocaleString()} ${t('bonus_points_earned', '포인트를 획득했습니다!')}`}
                    description={''}
                    showCancel={false}
                    showAccept={true}
                    onAccept={() => {
                        setShowBonusModal(false);
                        if (levelUpData) {
                            setTimeout(() => {
                                setShowLevelUpModal(true);
                            }, 500);
                        }
                    }}
                    acceptText={t('attendance_confirm', '확인')}
                />
            )}

            {/* Level Up Modal */}
            <LevelUpModal
                open={showLevelUpModal}
                onClose={() => setShowLevelUpModal(false)}
                levelUpData={levelUpData}
            />
        </Box>
    );
};

export default FeaturedGridPosts;
